import * as Resources from "Search/Scripts/Resources/TFS.Resources.Search.Scenarios";
import * as _SearchSharedContracts from "Search/Scripts/Generated/Search.SharedLegacy.Contracts";
import { TfsContext, IRouteData } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import { UrlParams } from "Search/Scenarios/Hub/NavigationHandler";
import { ignoreCaseComparer, format, base64Encode } from "VSS/Utils/String";
import { VersionControlType, CodeResult } from "Search/Scenarios/WebApi/Code.Contracts";
import { TelemetryConstants, TfvcRootPath, GitRootPath } from "Search/Scenarios/Shared/Constants";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPageContext } from "VSS/Platform/Context";

 export function deserialize<T>(dataString: string): T {
    let object: T = {} as T;

    if (!dataString || dataString === "") {
        return;
    }

    try {
        object = JSON.parse(dataString) as T;
    }
    catch (error) {
        dataString.split("}")
            .filter(c => !!c)
            .forEach((category: string) => {
                const nameValues = category.split("{");
                if (nameValues.length === 2) {
                    object[nameValues[0]] = nameValues[1].split("*").filter(s => !!s);
                }
            });
    }

    return object;
}

 export function serializeFilters(object: { [id: string]: string[] }): string {
    let filterString = "";
    if (!!object) {
        Object.keys(object)
            .forEach(key => {
                const filterValues = object[key].join("*");
                filterString = `${filterString}${key}{${filterValues}}`;
            });
    }

    return filterString;
}

 export function serializeSortOptions(sortOptions: _SearchSharedContracts.EntitySortOption[]): string {
    return JSON.stringify(sortOptions);
}

 export function areSortOptionsEqual(
    sortOptionsFromUrl: _SearchSharedContracts.EntitySortOption[],
    currentSortOptions: _SearchSharedContracts.EntitySortOption[]): boolean {
    // If both the sort options are null return true and if any one of them is null return false
    if (!sortOptionsFromUrl && !currentSortOptions) {
        return true;
    }

    if (!sortOptionsFromUrl &&
        currentSortOptions &&
        currentSortOptions.length === 0) {
        return true;
    }

    if (!currentSortOptions
        && sortOptionsFromUrl
        && sortOptionsFromUrl.length === 0) {
        return true;
    }

    if (!sortOptionsFromUrl || !currentSortOptions) {
        return false;
    }

    if (sortOptionsFromUrl.length !== currentSortOptions.length) {
        return false;
    }

    return !sortOptionsFromUrl.some(sortOptionInUrl => {
        return !currentSortOptions.some(currentSortOption => currentSortOption.field.toLowerCase() === sortOptionInUrl.field.toLowerCase() &&
            currentSortOption.sortOrder.toLowerCase() === sortOptionInUrl.sortOrder.toLowerCase());
    });
}

 export function areFiltersEqual(left: IDictionaryStringTo<string[]>, right: IDictionaryStringTo<string[]>): boolean {
    deleteEmptyFilters(left);
    deleteEmptyFilters(right);
    // If both the left and right are null return true and if any one of them is null return false
    if (!left && !right) {
        return true;
    }

    if (!left && right && Object.keys(right).length === 0) {
        return true;
    }

    if (!right && left && Object.keys(left).length === 0) {
        return true;
    }

    if (!left || !right) {
        return false;
    }

    const leftKeys = Object.keys(left),
        rightKeys = Object.keys(right);

    if (leftKeys.length !== rightKeys.length) {
        return false;
    }

    // keys in both left and right arrays objects should be same.
    const areKeysEqual = !leftKeys.some(lk => rightKeys.indexOf(lk) < 0);

    if (!areKeysEqual) {
        return false;
    }

    // There doesn't exist a key in leftKeys for which the corresponding array of values in "right" has different number of items or are un-equal
    const areFiltersEqual = !leftKeys.some(lk => left[lk].length !== right[lk].length || left[lk].some(lkv => right[lk].indexOf(lkv) < 0));

    return areFiltersEqual;
}

 export function isEmpty(obj): boolean {
    for (const prop in obj) {
        if (obj.hasOwnProperty(prop)) {
            return false;
        }
    }

    return JSON.stringify(obj) === JSON.stringify({});
}

/**
 * Compares two string for the occurrence of the searchText
 * Ranks string with exact match higher, then the one in which searchText substring occurs quite early
 * if there is a tie, ranks the two strings based on lexicographic order.
 * @param first
 * @param second
 * @param searchText
 */
 export function compare(first: string, second: string, searchText: string): number {
    first = first.toLowerCase();
    second = second.toLowerCase();
    searchText = searchText.toLowerCase();

    if (first === searchText) {
        return -1;
    }

    if (second === searchText) {
        return 1;
    }

    const substringIndexFirst = first.indexOf(searchText),
        substringIndexSecond = second.indexOf(searchText);

    if (substringIndexFirst !== substringIndexSecond) {
        return substringIndexFirst - substringIndexSecond;
    }
    else {
        return ignoreCaseComparer(first, second);
    }
}

 export function getAccountContextUrl(searchText: string, entityTypeUrlParam: string) {
    const collectionName = TfsContext.getDefault().contextData.collection.name,
        params: UrlParams = {
            type: entityTypeUrlParam,
            text: searchText,
            project: null
        } as UrlParams;

    return TfsContext.getDefault().getCollectionActionUrl(collectionName, "", "search", params as IRouteData);
}

 function deleteEmptyFilters(filters: IDictionaryStringTo<string[]>) {
    for (const key in filters) {
        if (filters[key].length <= 0) {
            delete filters[key];
        }
    }
}

 export interface TrackingData {
    [key: string]: {};
}

 export function getUrlWithTrackingData(url: string, data: TrackingData): string {
    return url + format("{0}{1}={2}",
                (url.indexOf("?") >= 0) ? "&" : "?",
                TelemetryConstants.UrlParameterKeyTrackingData,
                encodeTrackingData(data));
}

 function encodeTrackingData(data: TrackingData): string {
    return encodeURIComponent(base64Encode(JSON.stringify(data)));
}

 export function constructCompleteOrgSearchURL(url: string, searchText: string, source: string, type: string): string  {
    url = `${url}_search?queryText=${searchText}&type=${type}`;
    const queryUrlWithTrackingData = getUrlWithTrackingData(url, {
        source: source
            });
    return queryUrlWithTrackingData;
}

export function getWindowTitle(entityLabel: string, searchText?: string): string {
    const tfsContext = TfsContext.getDefault();
    const isHosted = tfsContext.isHosted;
    const pageTitle = isHosted ? Resources.DevOpsHostedSearchPageTitle : Resources.OnPremSearchPageTitle;

    return searchText
        ? `${searchText} - ${entityLabel} - ${pageTitle}`
        : `${entityLabel} - ${pageTitle}`;
}

export function isVCType(itemType: VersionControlType): boolean {
    return isGitType(itemType) || isTfvcType(itemType);
}

export function isGitType(itemType: VersionControlType): boolean {
    return ignoreCaseComparer(itemType.toString(), VersionControlType[VersionControlType.Git].toString()) === 0 || itemType === VersionControlType.Git;
}

export function isTfvcType(itemType: VersionControlType): boolean {
    return ignoreCaseComparer(itemType.toString(), VersionControlType[VersionControlType.Tfvc].toString()) === 0 || itemType === VersionControlType.Tfvc;
}

export function getFileExtension(path: string) {
    let extension = "";
    const dotIndex = (path || "").lastIndexOf(".");

    if (dotIndex >= 0) {
        extension = path.substring(dotIndex + 1);
    }
    return extension;
}

export function isGitRepo(repoName: string): boolean {
    return repoName.indexOf(TfvcRootPath) < 0;
}

export function constructLinkToContentFromRouteId(pageContext: IVssPageContext, item: CodeResult): string {
    const {path, project, repository, vcType, branch} = item;
    const routeValues: { [key: string]: string } = { path, project, "vc.GitRepositoryName": repository, _a: "contents" };

    let routeId: string | undefined;
    if (isGitType(vcType)) {
        routeId = "ms.vss-code-web.files-route-git";
        routeValues.version = `GB${branch}`;
    } else if (isTfvcType(vcType)) {
        routeId = "ms.vss-code-web.tfvc-files-route";
        routeValues.version = "T";
    }

    return routeId ? routeUrl(pageContext, routeId, routeValues) : undefined;
}

export function constructLinkToContent(item: CodeResult): string {
    let fileLink: string;
    const { project, path, repository, branch } = item;

    // ToDo: Need to see if this violates search across collection scenarios.
    const tfsContext = TfsContext.getDefault();

    if (isTfvcType(item.vcType)) {
        fileLink = tfsContext.navigation.serviceHost.uri
            + encodeURIComponent(project) + "/_versionControl" + "?path="
            + encodeURIComponent(path.split("\\").join("/")) + "&version="
            + encodeURIComponent("T") + "&_a=contents";
    } else if (isGitType(item.vcType)) {
        fileLink = tfsContext.navigation.serviceHost.uri
            + encodeURIComponent(project) + "/_git/"
            + encodeURIComponent(repository) + "?path="
            + encodeURIComponent(path.split("\\").join("/")) + "&version="
            + encodeURIComponent(`GB${branch}`) + "&_a=contents";
    }

    return fileLink;
}

/**
 * Normalizes the path slashes.
 * @param path The input path
 * @param isGit Whether it's a Git or Tfvc path
 * @param rootPath The root path to use if empty
 * Copied from /Tfs/Service/WebAccess/VersionControl/Scripts/VersionControlPath.ts
 */
export function normalizePath(path: string, isGit: boolean, rootPath: string, pathSeparator: string): string {
    if (!path) {
        return rootPath;
    }

    let normalizedPath = path.trim();

    normalizedPath = normalizedPath.replace(/[\/\\]+/g, pathSeparator);

    if (normalizedPath[normalizedPath.length - 1] === pathSeparator) {
        normalizedPath = normalizedPath.substring(0, normalizedPath.length - 1);
    }

    if (isGit) {
        if (normalizedPath[0] !== pathSeparator) {
            normalizedPath = pathSeparator + normalizedPath;
        }
    } else {
        if (normalizedPath === "$") {
            normalizedPath = TfvcRootPath;
        }
    }

    return normalizedPath;
}

/**
 * Gets the containing directory of the last path component.
 * @param path The path
 * @returns Path characters up to the last slash.
 *          The trailing slash is included if the result is the root directory ("/" or "$/").
 *          Otherwise, the trailing slash is not included.
 *          Empty string if the path contains no slash.
 * Copied from : /Tfs/Service/WebAccess/VersionControl/Scripts/VersionControlPath.ts
 */
function getFolderName(path: string, pathSeparator: string) {
    let pos: number;
    let folder: string;

    if (path) {
        pos = path.lastIndexOf(pathSeparator);
        if (pos >= 0) {
            folder = path.substr(0, pos);

            // Always include a '/' for the root folder
            if (folder === "$") {
                return TfvcRootPath;
            }
            else if (folder === "") {
                return GitRootPath;
            }

            return folder;
        }
    }

    return "";
}

/**
 * Gets a list with all the parent full paths of a provided path.
 * @param path The path
 * @returns A list with parent paths, excluding the provided one.
 * @example ("/a/b/c") returns ["/a/b", "/a", "/"]
 * Copied from /Tfs/Service/WebAccess/VersionControl/Scripts/VersionControlPath.ts
 */
export function getParentPaths(path: string, pathSeparator: string) {
    const partials: string[] = [];

    if (path) {
        let previous = path;
        let parent = getFolderName(previous, pathSeparator);
        while (previous !== parent) {
            partials.push(parent);
            previous = parent;
            parent = getFolderName(previous, pathSeparator);
        }
    }

    return partials;
}